import type { Questionnaire } from '@medplum/fhirtypes'
import {
  QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION,
  VALIDATED_TOOL_SUMMARY_ITEM,
  VALIDATED_TOOL_TAG,
} from '#src/models/questionnaires/utils/index.ts'
import {
  QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
  QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
} from '#src/structureDefinitions.ts'

export const iadlQuestionnaire = {
  resourceType: 'Questionnaire',
  url: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
  title: 'Instrumental Activities of Daily Life Questionnaire (IADL)',
  name: 'IADL',
  status: 'active',
  subjectType: ['Patient'],
  meta: {
    profile: ['http://hl7.org/fhir/4.0/StructureDefinition/Questionnaire'],
    tag: [
      VALIDATED_TOOL_TAG,
      {
        code: 'lformsVersion: 36.7.0',
      },
    ],
  },
  code: [
    {
      system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
      code: 'iadl-title',
      display: 'Instrumental Activities of Daily Life Questionnaire (IADL)',
    },
  ],
  item: [
    {
      linkId: QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
      type: 'display',
      text: 'IADL PDF',
      extension: [
        {
          url: QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
          valueString:
            'https://www.alz.org/careplanning/downloads/lawton-iadl.pdf',
        },
      ],
    },
    {
      linkId: 'phone-use',
      type: 'choice',
      text: 'Ability to Use Telephone',
      extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
      code: [
        {
          system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
          code: 'iadl-phone-use',
          display: 'Ability to Use Telephone',
        },
      ],
      answerOption: [
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-phone-use-1',
            display:
              'Operates telephone on own initiative-looks up and dials numbers, etc.',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-phone-use-2',
            display: 'Dials a few well-known numbers.',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-phone-use-3',
            display: 'Answers telephone but does not dial.',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '4',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-phone-use-4',
            display: 'Does not use telephone at all.',
          },
        },
      ],
    },
    {
      linkId: 'shopping',
      type: 'choice',
      text: 'Shopping',
      extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
      code: [
        {
          system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
          code: 'iadl-shopping',
          display: 'Shopping',
        },
      ],
      answerOption: [
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-shopping-1',
            display: 'Takes care of all shopping needs independently.',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-shopping-2',
            display: 'Shops independently for small purchases.',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-shopping-3',
            display: 'Needs to be accompanied on any shopping trip.',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '4',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-shopping-4',
            display: 'Completely unable to shop.',
          },
        },
      ],
    },
    {
      linkId: 'food-preparation',
      type: 'choice',
      text: 'Food Preparation',
      extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
      code: [
        {
          system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
          code: 'iadl-food-preparation',
          display: 'Food Preparation',
        },
      ],
      answerOption: [
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-food-preparation-1',
            display: 'Plans, prepares and serves adequate meals independently',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-food-preparation-2',
            display: 'Prepares adequate meals if supplied with ingredients',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-food-preparation-3',
            display:
              'Heats, serves and prepares meals, or prepares meals but does not maintain adequate diet',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '4',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-food-preparation-4',
            display: 'Needs to have meals prepared and served',
          },
        },
      ],
    },
    {
      linkId: 'housekeeping',
      type: 'choice',
      text: 'Housekeeping',
      extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
      code: [
        {
          system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
          code: 'iadl-housekeeping',
          display: 'Housekeeping',
        },
      ],
      answerOption: [
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-housekeeping-1',
            display: 'Maintains house alone or with occasional assistance',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-housekeeping-2',
            display:
              'Performs light daily tasks such as dishwashing, bed making',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-housekeeping-3',
            display:
              'Performs light daily tasks but cannot maintain acceptable level of cleanliness',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '4',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-housekeeping-4',
            display: 'Needs help with all home maintenance tasks',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '5',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-housekeeping-5',
            display: 'Does not participate in any housekeeping tasks',
          },
        },
      ],
    },
    {
      linkId: 'laundry',
      type: 'choice',
      text: 'Laundry',
      extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
      code: [
        {
          system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
          code: 'iadl-laundry',
          display: 'Laundry',
        },
      ],
      answerOption: [
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-laundry-1',
            display: 'Does personal laundry completely',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-laundry-2',
            display: 'Launders small items—rinses stockings, etc.',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-laundry-3',
            display: 'All laundry must be done by others',
          },
        },
      ],
    },
    {
      linkId: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
      type: 'display',
      prefix: 'Laundry Description',
      text: '1. Does personal laundry completely.\n\n2. Launders small items—rinses stockings, etc.\n\n3. All laundry must be done by others.',
      extension: [
        {
          url: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
          valueString: '{ "accordion": "true" }',
        },
      ],
    },
    {
      linkId: 'mode-of-transportation',
      type: 'choice',
      text: 'Mode of Transportation',
      extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
      code: [
        {
          system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
          code: 'iadl-mode-of-transportation',
          display: 'Mode of Transportation',
        },
      ],
      answerOption: [
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-mode-of-transportation-1',
            display:
              'Travels independently on public transportation or drives own car',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-mode-of-transportation-2',
            display:
              'Arranges own travel via taxi, but does not otherwise use public transportation',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-mode-of-transportation-3',
            display:
              'Travels on public transportation when accompanied by another',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '4',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-mode-of-transportation-4',
            display:
              'Travel limited to taxi or automobile with assistance of another',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '5',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-mode-of-transportation-5',
            display: 'Does not travel at all',
          },
        },
      ],
    },
    {
      linkId: 'medication-responsibility',
      type: 'choice',
      text: 'Responsibility for Own Medications',
      extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
      code: [
        {
          system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
          code: 'iadl-medication-responsibility',
          display: 'Responsibility for Own Medications',
        },
      ],
      answerOption: [
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-medication-responsibility-1',
            display:
              'Is responsible for taking medication in correct dosages at correct time',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-medication-responsibility-2',
            display:
              'Takes responsibility if medication is prepared in advance in separate dosages',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-medication-responsibility-3',
            display: 'Is not capable of dispensing own medication',
          },
        },
      ],
    },
    {
      linkId: 'financial-management',
      type: 'choice',
      text: 'Ability to Handle Finances',
      extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
      code: [
        {
          system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
          code: 'iadl-financial-management',
          display: 'Ability to Handle Finances',
        },
      ],
      answerOption: [
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-financial-management-1',
            display: 'Manages financial matters independently',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-financial-management-2',
            display:
              'Manages day-to-day purchases, but needs help with banking, major purchases, etc.',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
          valueCoding: {
            system: 'https://www.seenhealth.org/fhir/Questionnaire/IADL',
            code: 'iadl-financial-management-3',
            display: 'Incapable of handling money',
          },
        },
      ],
    },
    VALIDATED_TOOL_SUMMARY_ITEM,
  ],
} satisfies Questionnaire
